/**
 * Shared domain types. Framework-agnostic — no mongoose, no express.
 * These describe the *normalised* shapes used across the API, bot and admin panel.
 *
 * Every list of literals is declared `as const` so it can act as the single
 * source of truth for both the TypeScript union type and the Zod enum in
 * `schemas.ts`.
 */

// ─────────────────────────────────────────────────────────────
// Enums (as const arrays + derived union types)
// ─────────────────────────────────────────────────────────────

export const AUTH_METHODS = ['bearer', 'api_key_header', 'query_param', 'basic'] as const;
export type AuthMethod = (typeof AUTH_METHODS)[number];

export const OUR_FIELDS = [
  'name',
  'price',
  'currency',
  'inStock',
  'stockCount',
  'externalId',
  'duration',
  'region',
  'accessType',
  'warrantyDays',
  'description',
  'category',
] as const;
export type OurField = (typeof OUR_FIELDS)[number];

export const FIELD_TRANSFORMS = [
  'toString',
  'toNumber',
  'toBoolean',
  'toLowerCase',
  'toUpperCase',
  'parseDuration',
] as const;
export type FieldTransform = (typeof FIELD_TRANSFORMS)[number];

export const ACCESS_TYPES = [
  'shared_account',
  'private_account',
  'invite',
  'edu',
  'unknown',
] as const;
export type AccessType = (typeof ACCESS_TYPES)[number];

/**
 * Human-facing label for each access type — shown to customers in the bot and
 * to admins in the product form. `unknown` (the fallback when a supplier
 * doesn't specify one) reads as "Instant" rather than the internal literal,
 * since an unspecified access type is almost always an instantly-delivered
 * code/key rather than a shared/private account.
 */
export const ACCESS_TYPE_LABELS: Record<AccessType, string> = {
  shared_account: 'Shared account',
  private_account: 'Private account',
  invite: 'Invite',
  edu: 'Edu',
  unknown: 'Instant',
};

export const ORDER_STATUSES = [
  'awaiting_payment',
  'verifying_payment',
  'pending',
  'processing',
  'completed',
  'failed',
  'cancelled',
  'refunded',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const TRANSACTION_TYPES = ['sale', 'refund', 'supplier_charge'] as const;
export type TransactionType = (typeof TRANSACTION_TYPES)[number];

/** Bot UI languages. Extend this list to add another language. */
export const LANGUAGES = ['en', 'ur'] as const;
export type Language = (typeof LANGUAGES)[number];

export const WALLET_TX_TYPES = ['topup', 'order_payment', 'refund', 'adjustment'] as const;
export type WalletTxType = (typeof WALLET_TX_TYPES)[number];

export const WALLET_TX_STATUSES = ['pending', 'verified', 'rejected'] as const;
export type WalletTxStatus = (typeof WALLET_TX_STATUSES)[number];

/** On-chain USDT networks auto-verified via Binance's deposit-history API. */
export const PAYMENT_NETWORKS = ['TRC20', 'BEP20'] as const;
export type PaymentNetwork = (typeof PAYMENT_NETWORKS)[number];

/**
 * Which adapter implementation drives a supplier.
 *  - `generic`       : GenericHttpAdapter, fully config-driven — works for most APIs.
 *  - `insightxstore` : bespoke adapter for the InsightX Store REST API
 *                      (`/api/v1/products|balance|orders`).
 *  - `warzoneshop`   : bespoke adapter for the Warzone Shop API
 *                      (`/api/v1/products|me|order`).
 */
export const SUPPLIER_PRESETS = ['generic', 'insightxstore', 'warzoneshop', 'aivaulthub', 'xproseller'] as const;
export type SupplierPreset = (typeof SUPPLIER_PRESETS)[number];

// ─────────────────────────────────────────────────────────────
// Supplier
// ─────────────────────────────────────────────────────────────

export interface SupplierApiConfig {
  /** Base URL of the supplier API. Encrypted at rest. */
  baseUrl: string;
  /** Secret API key / token. Encrypted at rest. Never returned to clients. */
  apiKey: string;
  authMethod: AuthMethod;
  /** Adapter implementation. Defaults to `generic`. */
  preset?: SupplierPreset;
  /** Header name when authMethod === 'api_key_header'. e.g. "X-API-Key". */
  authHeaderName?: string;
  /** Query param name when authMethod === 'query_param'. e.g. "token". */
  authQueryParam?: string;
  /** Optional path (relative to baseUrl) that returns the product list. */
  productsPath?: string;
  /** Dot-path inside the products response where the product array exists. */
  productsArrayPath?: string;
  /** Optional path template for creating an order. `{externalId}` / `{quantity}` are substituted. */
  createOrderPath?: string;
  /** Optional path template for reading an order status. `{orderId}` is substituted. */
  orderStatusPath?: string;
  /** Optional path template for cancelling an order. `{orderId}` is substituted. */
  cancelOrderPath?: string;
  /** Optional path that returns the wallet balance. */
  balancePath?: string;
  /** Optional path that returns the account profile + ledger. Defaults to balancePath. */
  accountPath?: string;
  /** Dot-path into the balance response that holds the numeric balance. */
  balancePathField?: string;
  /** Dot-path into the account response that holds the ledger/transactions array. */
  ledgerPathField?: string;
  /** Body key for the product id on create-order. Default: sends product_id/productId/sku/… */
  orderProductParam?: string;
  /** Body key for the quantity on create-order. Default: sends quantity/qty/count. */
  orderQuantityParam?: string;
  /** Whether the product id should be sent as a number when it is numeric. Default: true. */
  orderProductIdNumeric?: boolean;
  /** Dot-path into the create-order response that holds the external order id. */
  orderIdPathField?: string;
  /** Dot-path into the order-status response that holds the status string. */
  orderStatusPathField?: string;
  /** Dot-path into the order-status/create response that holds delivery data (codes, credentials…). */
  deliveryDataPathField?: string;
}

export interface SupplierLedgerEntry {
  type: string;
  amountUsdt: number;
  balanceAfterUsdt: number | null;
  note: string;
  createdAt: string | null;
}

export interface SupplierAccount {
  balanceUsdt: number;
  currency: string;
  ledger: SupplierLedgerEntry[];
  /** The raw account/balance response for debugging. */
  raw: unknown;
}

/** What an adapter returns from a successful order placement, normalised for the bot. */
export interface SupplierOrderResult {
  externalOrderId: string;
  status: OrderStatus;
  /** Delivered codes / keys, when the supplier returns them inline. */
  codes: string[];
  /** Any structured delivery payload (credentials, links, notes…). */
  deliveryData: Record<string, unknown> | null;
  raw: unknown;
}

export interface SupplierFieldMapping {
  ourField: OurField;
  /** JSONPath-like string into the supplier response, e.g. "data.items[0].price". */
  supplierPath: string;
  transform?: FieldTransform;
  /** Optional numeric scaling applied after transformation (e.g. 0.01 converts cents to USDT). */
  multiplier?: number;
}

export interface Supplier {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  walletAlertThreshold: number;
  apiConfig: SupplierApiConfig;
  fieldMappings: SupplierFieldMapping[];
  syncIntervalMinutes: number;
  lastSyncedAt: string | null;
  walletBalance: number | null;
  lastSyncError: string | null;
  productCount?: number;
  createdAt: string;
  updatedAt: string;
}

/** Supplier as returned by the admin API — secrets stripped. */
export interface SupplierPublic extends Omit<Supplier, 'apiConfig'> {
  apiConfig: Omit<SupplierApiConfig, 'apiKey' | 'baseUrl'> & {
    /** Always masked. */
    apiKey: '••••••••' | '';
    baseUrl: string;
  };
}

// ─────────────────────────────────────────────────────────────
// Normalised product
// ─────────────────────────────────────────────────────────────

export interface NormalisedProductSupplierEntry {
  supplierId: string;
  externalProductId: string;
  costUsdt: number;
  inStock: boolean;
  stockCount: number;
  lastCheckedAt: string;
}

export interface NormalisedProduct {
  id: string;
  name: string;
  slug: string;
  durationDays: number;
  accessType: AccessType;
  /** 'global' or ISO 3166-1 alpha-2. */
  region: string;
  warrantyDays: number;
  category: string;
  tags: string[];
  description: string;
  isActive: boolean;
  /** Icon/photo shown on the product's detail card in the bot. Plain URL, empty = none. */
  imageUrl: string;
  suppliers: NormalisedProductSupplierEntry[];
  /** Manual price override. If set, commission tiers are ignored. */
  pricingOverride: number | null;
  createdAt: string;
  updatedAt: string;
}

// ─────────────────────────────────────────────────────────────
// Price engine
// ─────────────────────────────────────────────────────────────

export interface PriceQuote {
  productId: string;
  supplierId: string;
  externalProductId: string;
  costUsdt: number;
  commissionUsdt: number;
  sellingPriceUsdt: number;
  inStock: boolean;
  /** Ordered fallback supplier ids (cheapest first, excluding the selected one). */
  fallbackSupplierIds: string[];
}

// ─────────────────────────────────────────────────────────────
// Order
// ─────────────────────────────────────────────────────────────

export interface Order {
  id: string;
  telegramUserId: number;
  telegramUsername: string | null;
  productId: string;
  supplierId: string;
  externalOrderId: string | null;
  status: OrderStatus;
  supplierCostUsdt: number;
  commissionUsdt: number;
  sellingPriceUsdt: number;
  deliveryData: Record<string, unknown> | null;
  idempotencyKey: string;
  attempts: number;
  lastAttemptAt: string | null;
  failureReason: string | null;
  webhookPayloads: unknown[];
  createdAt: string;
  updatedAt: string;
}

export interface Transaction {
  id: string;
  orderId: string;
  type: TransactionType;
  amountUsdt: number;
  supplierCostUsdt: number;
  profitUsdt: number;
  note: string;
  createdAt: string;
}

export interface TelegramUser {
  id: string;
  telegramId: number;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  language: string;
  totalOrders: number;
  totalSpentUsdt: number;
  walletBalanceUsdt: number;
  isBlocked: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WalletTransaction {
  id: string;
  telegramUserId: number;
  type: WalletTxType;
  status: WalletTxStatus;
  /** Positive = credit (topup, refund), negative = debit (order_payment). */
  amountUsdt: number;
  balanceAfterUsdt: number | null;
  /** Binance TxID for topups, order id for order_payment, free text for adjustments. */
  reference: string;
  /** 'auto' | 'admin:<username>' | null while pending. */
  verifiedBy: string | null;
  note: string;
  createdAt: string;
  updatedAt: string;
}

export interface CommissionTier {
  id: string;
  minCostUsdt: number;
  maxCostUsdt: number;
  fixedUsdt: number | null;
  percentage: number | null;
  createdAt: string;
  updatedAt: string;
}

// ─────────────────────────────────────────────────────────────
// Adapter contracts (raw, un-normalised supplier payloads)
// ─────────────────────────────────────────────────────────────

export interface RawSupplierProduct {
  externalId: string;
  name: string;
  price: number;
  currency: string;
  inStock: boolean;
  stockCount?: number;
  durationDays?: number;
  region?: string;
  accessType?: AccessType;
  warrantyDays?: number;
  description?: string;
  category?: string;
  /** The original supplier object, kept for debugging. */
  raw: unknown;
}

export interface RawSupplierOrder {
  externalOrderId: string;
  status: OrderStatus;
  deliveryData?: Record<string, unknown>;
  /** Delivered codes / keys / links, when the supplier returns them. */
  codes?: string[];
  raw: unknown;
}

export interface RawOrderStatus {
  status: OrderStatus;
  deliveryData?: Record<string, unknown>;
  codes?: string[];
  raw: unknown;
}

// ─────────────────────────────────────────────────────────────
// Stats / dashboard
// ─────────────────────────────────────────────────────────────

export interface DashboardStats {
  todayRevenueUsdt: number;
  todayOrders: number;
  todayProfitUsdt: number;
  activeSuppliers: number;
  pendingOrders: number;
  totalProducts: number;
}

export interface RevenuePoint {
  date: string; // YYYY-MM-DD
  revenueUsdt: number;
  profitUsdt: number;
  orders: number;
}

// ─────────────────────────────────────────────────────────────
// Auth
// ─────────────────────────────────────────────────────────────

export interface AuthLoginResponse {
  token: string;
  expiresIn: string;
  username: string;
}

export interface JwtAdminPayload {
  sub: string;
  username: string;
  role: 'admin';
}

// ─────────────────────────────────────────────────────────────
// Generic API envelope
// ─────────────────────────────────────────────────────────────

export interface ApiError {
  error: string;
  message: string;
  details?: unknown;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}
